import { createFileRoute, Outlet, useChildMatches, useParams } from '@tanstack/react-router';
import { z } from 'zod';

import { SessionDetailScreen } from '@/features/sessions/components/shared/SessionDetailScreen';
import { requirePermission } from '@/lib/guards';

const searchSchema = z.object({
  tab: z.enum(['notes', 'case-reports', 'feedback']).catch('notes').optional(),
});

export const Route = createFileRoute('/_staff/admin/workspace/sessions/$sessionId')({
  beforeLoad: () => {
    requirePermission('session:read');
  },
  component: WorkspaceSessionDetailRoute,
  staticData: { crumb: 'Session' },
  validateSearch: searchSchema.parse.bind(searchSchema),
});

/**
 * The note + case-report routes are nested children of this route under
 * TanStack Router's file-based dot-syntax (`$sessionId.note.tsx` registers
 * as a child of `$sessionId.tsx`, not a sibling). Without rendering
 * `<Outlet />`, navigating to `/sessions/:id/note` matches the child but
 * never surfaces it — the parent re-renders with no `?tab=` query and
 * defaults back to the Overview tab. Branching on `useChildMatches()`
 * surfaces the child when one matches and otherwise renders the normal
 * detail screen.
 */
function WorkspaceSessionDetailRoute() {
  const { sessionId } = useParams({ from: '/_staff/admin/workspace/sessions/$sessionId' });
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;
  return <SessionDetailScreen sessionId={sessionId} surface="workspace" />;
}
